import React, { useState } from 'react'

import styled from '@emotion/styled'
import {
  AlignItems,
  BorderStyle,
  Box,
  Button,
  Color,
  Display,
  FlexDirection,
  FontFamily,
  FontWeight,
  Heading3,
  JustifyContent,
  RadiusScale,
  Space,
  Text,
  Visible,
} from '@gousto-internal/citrus-react'
import { useDispatch } from 'react-redux'

import { trackUTMAndPromoCode } from 'actions/tracking'
import { boxPricesClickRecipeNumber } from 'actions/trackingKeys'
import { useIsOptimizelyFeatureEnabled } from 'containers/OptimizelyRollouts'
import { boxTypes, cta } from 'routes/BoxPrices/boxPricesConfig'
import { Benefits, BenefitType } from 'routes/Home/Benefits'

import { FixedCtaContainer } from '../BoxPricesList/FixedCtaContainer/FixedCtaContainer'
import { boxPricesBoxSizeSelected } from '../boxPricesActions'
import { applySaveAndSavourDiscount, isNumberString } from '../boxPricesUtils'
import { BoxComparisonTable, BoxComparisonTableHeader } from './BoxPriceComparisonTable'
import { BoxPriceSuitableForSection } from './BoxPriceSuitableForSection'
import { BoxPriceBlockProps, BoxComparisonHeaderType } from './boxPriceBlockModels'

const AnimatedButton = styled(Button)({
  transition: 'all 0.2s ease-in-out',
  transitionProperty: 'border, background',
})

const BoxPriceBlock = ({ boxInfo, numPersons, isSelected }: BoxPriceBlockProps) => {
  const recipesLeftTrackingIndex = boxInfo.length - 1
  const [selectedIndex, setSelectedIndex] = useState(recipesLeftTrackingIndex)
  const { title, boxSizeTrackingValue } = boxTypes[numPersons]

  const dispatch = useDispatch()

  const boxPricesBoxSizeSelect = () => dispatch(boxPricesBoxSizeSelected(numPersons))

  const moreRecipesHeader = `Select ${recipesLeftTrackingIndex - selectedIndex} more recipe${
    selectedIndex === recipesLeftTrackingIndex - 1 ? '' : 's'
  } for the best price`
  const bestPriceHeader = 'You’ve got the best price per portion!'

  const isBestPrice = selectedIndex === recipesLeftTrackingIndex

  const {
    num_portions: numPortions,
    price_per_portion: pricePerPortion,
    total: pricePerBox,
  } = boxInfo[selectedIndex]

  const { saveAndSavourPricePerBox, saveAndSavourPricePerPortion } =
    isNumberString(pricePerBox) && isNumberString(pricePerPortion)
      ? applySaveAndSavourDiscount(pricePerBox, numPortions * numPersons, pricePerPortion)
      : {
          saveAndSavourPricePerBox: null,
          saveAndSavourPricePerPortion: null,
        }

  const dispatchTrackUTMAndPromoCode = (numRecipes: number) =>
    dispatch(
      trackUTMAndPromoCode(boxPricesClickRecipeNumber, {
        box_size: boxSizeTrackingValue,
        number_of_recipes: numRecipes,
      }),
    )

  // Here, we are not using this feature flag as part of an experiment but as a way to turn the changes related to Save And Savour price table on and off quickly, should it be the case
  // The changes related to Save and Savour are:
  // - Table section for Save and Savour under the existing table *To be implemented*
  // - Fixed CTA on screens < 640px wide
  // - New copy under the table *To be implemented*
  const isSaveAndSavourPricingEnabled = useIsOptimizelyFeatureEnabled(
    'beetroots_save_and_savour_price_breakdown_enabled',
  )

  const CtaBlock = (
    <>
      <AnimatedButton onClick={boxPricesBoxSizeSelect} color={Color.Secondary_600} width="100%">
        <Space size={10} direction="vertical" />
        {cta}
      </AnimatedButton>
      <Space size={2} direction="vertical" />
      <Benefits pickFilter={[BenefitType.NO_LOCK_IN]} />
    </>
  )

  return (
    <Box
      borderStyle={BorderStyle.Solid}
      borderColor={Color.ColdGrey_200}
      borderWidth={0.5}
      flexGrow={1}
      maxWidth={['100%', '45%', '372px']}
      paddingV={4}
      paddingH={[3, 5]}
      bg={Color.White}
      display={isSelected ? Display.Block : [Display.None, Display.Block]}
      data-testid="boxPricesBlock"
    >
      <Visible display={[Display.None, Display.Block]}>
        <Heading3>{title}</Heading3>
        <Space size={2} direction="vertical" />
      </Visible>
      <Space size={2} direction="vertical" />
      <Text size={2} fontWeight={FontWeight.Bold}>
        {boxTypes[numPersons].subhead}
      </Text>
      <Space size={2} direction="vertical" />
      <ul
        style={{
          padding: 0,
          listStyle: 'none',
        }}
      >
        <BoxPriceSuitableForSection suitableForLabel={boxTypes[numPersons].suitable} />
      </ul>
      <Text fontFamily={FontFamily.Bold} size={2}>
        Select number of recipes
      </Text>
      <Space size={2} direction="vertical" />

      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Row}
        justifyContent={JustifyContent.Center}
        alignItems={AlignItems.Center}
        wrap={false}
      >
        {boxInfo.map((item, index) => {
          const numRecipes = item.num_portions
          const buttonIsSelected = selectedIndex === index

          return (
            <Box
              key={numRecipes}
              itemType="button"
              onClick={() => {
                dispatchTrackUTMAndPromoCode(numRecipes)
                setSelectedIndex(index)
              }}
              color={buttonIsSelected ? Color.White : Color.ColdGrey_800}
              flexGrow={1}
              height={buttonIsSelected ? '48px' : '40px'}
              bg={buttonIsSelected ? Color.Secondary_400 : Color.White}
              borderStyle={BorderStyle.Solid}
              borderColor={Color.Secondary_400}
              borderRightWidth={index === boxInfo.length - 1 ? 0.5 : 0}
              borderLeftWidth={0.5}
              borderWidth={0.5}
              borderRadius={buttonIsSelected ? (2 as RadiusScale) : undefined}
              style={{ cursor: 'pointer' }}
              display={Display.Flex}
              alignItems={AlignItems.Center}
              justifyContent={JustifyContent.Center}
            >
              <Text size={3} fontWeight={FontWeight.Bold}>
                {numRecipes}
              </Text>
            </Box>
          )
        })}
      </Box>
      <Space size={5} direction="vertical" />
      <BoxComparisonTableHeader
        type={
          isBestPrice
            ? BoxComparisonHeaderType.BEST_PRICE
            : BoxComparisonHeaderType.SELECT_MORE_RECIPES
        }
        headerValue={isBestPrice ? bestPriceHeader : moreRecipesHeader}
      />
      <BoxComparisonTable
        headerType={BoxComparisonHeaderType.CLASSIC_GOUSTO}
        headerValue={`${selectedIndex + 2} classic Gousto recipes`}
        pricePerBox={pricePerBox || '--.--'}
        pricePerPortion={pricePerPortion || '--.--'}
        saveAndSavourEnabled={isSaveAndSavourPricingEnabled}
      />
      {isSaveAndSavourPricingEnabled && (
        <BoxComparisonTable
          headerType={BoxComparisonHeaderType.SAVE_AND_SAVOUR}
          headerValue={`${selectedIndex + 2} Save and Savour recipes`}
          pricePerBox={saveAndSavourPricePerBox || '--.--'}
          pricePerPortion={saveAndSavourPricePerPortion || '--.--'}
          saveAndSavourEnabled
        />
      )}
      <Space size={4} direction="vertical" />
      <Box>
        <Benefits pickFilter={[BenefitType.PAID_DELIVERY]} />
      </Box>
      <Space size={4} direction="vertical" />
      {isSaveAndSavourPricingEnabled ? (
        <Visible display={[Display.None, Display.Block]}>{CtaBlock}</Visible>
      ) : (
        CtaBlock
      )}

      {isSaveAndSavourPricingEnabled && (
        <Visible display={[Display.Block, Display.None]}>
          <FixedCtaContainer>{CtaBlock}</FixedCtaContainer>
        </Visible>
      )}
    </Box>
  )
}

export { BoxPriceBlock }
